import { createContext, useContext, useState, type ReactNode } from 'react';

// Leichter Kontext, über den die E-Mail-Seite ihre Navigation (Postfach + Ordner + "Neue E-Mail")
// in die globale Sidebar spiegelt — analog zu usePageControls der Insel-Pille. Die Sidebar rendert
// diese Daten als kontextuelle Sub-Nav, solange man im E-Mail-Bereich ist.

export type EmailOrdner = 'posteingang' | 'offen' | 'erledigt' | 'gesendet' | 'geplant' | 'entwuerfe' | 'wiedervorlage' | 'rueckmeldung' | 'werbung' | 'archiviert' | 'geloescht' | 'vorlagen' | 'serienmail' | 'einstellungen' | 'objekt';

export interface EmailKontoLite {
  id: string;
  anzeigename?: string | null;
  email_adresse: string;
  /** Für Kopf/Postfach-Karte im neuen Design (email_konten.bezeichnung / letzter_sync). */
  bezeichnung?: string | null;
  letzter_sync?: string | null;
}

export interface EmailNavState {
  konten: EmailKontoLite[];
  selectedKontoId: string | null;
  onSelectKonto: (id: string) => void;
  activeTab: EmailOrdner;
  onSelectTab: (t: EmailOrdner) => void;
  unreadCount: number;
  offenCount: number;
  wiedervorlageCount: number;
  /** #551: Mails, die auf eine Antwort warten */
  rueckmeldungCount: number;
  werbungCount: number;
  archiviertCount: number;
  entwuerfeCount: number;
  geplantCount: number;
  objekte: { id: string; objektnummer: string | null; bezeichnung: string | null; unread: number }[];
  selectedObjektId: string | null;
  onSelectObjekt: (id: string) => void;
  onAssignObjekt: (emailId: string, objektId: string) => void;
  /** #578: Mails per Maus auf einen Ordner ziehen (Offen, Erledigt, Werbung …). */
  onDropInOrdner?: (emailIds: string[], ordner: Exclude<EmailOrdner, 'objekt'>) => void;
  onCompose: () => void;
  /** Liste neu laden (Knopf „Aktualisieren“). */
  onRefresh?: () => void;
}

interface EmailNavContextValue {
  nav: EmailNavState | null;
  setNav: (n: EmailNavState | null) => void;
}

const EmailNavContext = createContext<EmailNavContextValue | null>(null);

export function EmailNavProvider({ children }: { children: ReactNode }) {
  const [nav, setNav] = useState<EmailNavState | null>(null);
  return <EmailNavContext.Provider value={{ nav, setNav }}>{children}</EmailNavContext.Provider>;
}

// Sichere Fallback-Variante, falls ein Consumer ohne Provider gemountet wird.
export function useEmailNav(): EmailNavContextValue {
  return useContext(EmailNavContext) ?? { nav: null, setNav: () => {} };
}
